/**
 * Dashboard Manager/Admin — point d'entrée synthétique (KPI) vers les 3 volets de suivi
 * campagne (process lot 1, transversal lot 2, features applicatives lot 3) + les
 * validations en attente. Chaque carte renvoie vers la vue détaillée correspondante
 * (Validation Dashboard / Campaigns > Suivi) ; ce n'est volontairement pas une
 * redondance des tableaux détaillés (cf. Campaigns > Suivi, qui reste la vue complète).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  campaignApi,
  transversalAssessmentApi,
  ApiRequestError,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";

type KpiCardProps = {
  value: string;
  label: string;
  detail?: string;
  to: string;
  accent?: string;
};

function KpiCard({ value, label, detail, to, accent }: KpiCardProps) {
  return (
    <Link
      to={to}
      className="sm-card sm-card-pad"
      style={{ display: "block", textDecoration: "none", color: "inherit" }}
    >
      <div style={{ fontSize: "2em", fontWeight: 800, color: accent ?? "#1e293b" }}>{value}</div>
      <div style={{ fontSize: "0.9em", fontWeight: 600, color: "#374151", marginTop: 4 }}>{label}</div>
      {detail ? (
        <div className="sm-muted" style={{ fontSize: "0.8em", marginTop: 4 }}>
          {detail}
        </div>
      ) : null}
    </Link>
  );
}

type DashboardState = {
  pendingProcess: number;
  pendingTransversal: number;
  processInProgress: number;
  processValidated: number;
  processTotal: number;
  transversalInProgress: number;
  transversalValidated: number;
  transversalTotal: number;
  featuresCompleted: number;
  featuresTotal: number;
};

export function DashboardPage() {
  const { t } = useTranslation();
  const yearNow = new Date().getFullYear();
  const [year, setYear] = useState(yearNow);
  const [data, setData] = useState<DashboardState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      assessmentApi.list({ status: "submitted", year }),
      transversalAssessmentApi.list({ status: "submitted", year }),
      campaignApi.processTracking(year),
      campaignApi.transversalTracking(year),
      campaignApi.featuresTracking(year),
    ])
      .then(([pendingProcessRes, pendingTransversalRes, processRes, transversalRes, featuresRes]) => {
        if (cancelled) return;
        let featuresCompleted = 0;
        let featuresTotal = 0;
        for (const d of featuresRes.tracking.domains) {
          for (const cell of d.cells) {
            featuresCompleted += cell.completed;
            featuresTotal += cell.total;
          }
        }
        setData({
          pendingProcess: pendingProcessRes.assessments.length,
          pendingTransversal: pendingTransversalRes.assessments.length,
          processInProgress: processRes.tracking.totals.inProgress,
          processValidated: processRes.tracking.totals.validated,
          processTotal: processRes.tracking.totals.total,
          transversalInProgress: transversalRes.tracking.totals.inProgress,
          transversalValidated: transversalRes.tracking.totals.validated,
          transversalTotal: transversalRes.tracking.totals.total,
          featuresCompleted,
          featuresTotal,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, t]);

  const pendingTotal = data ? data.pendingProcess + data.pendingTransversal : 0;
  const processPct =
    data && data.processTotal > 0 ? Math.round((data.processValidated / data.processTotal) * 100) : null;
  const transversalPct =
    data && data.transversalTotal > 0
      ? Math.round((data.transversalValidated / data.transversalTotal) * 100)
      : null;
  const featuresPct =
    data && data.featuresTotal > 0 ? Math.round((data.featuresCompleted / data.featuresTotal) * 100) : null;

  return (
    <div className="sm-page">
      <PageHeader title={t("dashboard_title")} subtitle={t("dashboard_subtitle")} />

      <div className="sm-card sm-card-pad" style={{ display: "inline-block", marginBottom: 16 }}>
        <label className="sm-label" style={{ margin: 0 }}>
          {t("year")}
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 100, marginLeft: 8 }}
          />
        </label>
      </div>

      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? <p className="sm-muted">{t("loading")}</p> : null}

      {!loading && data ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
          }}
        >
          <KpiCard
            value={String(pendingTotal)}
            label={t("dashboard_kpi_pending_validations")}
            detail={t("dashboard_kpi_pending_validations_detail", {
              process: data.pendingProcess,
              transversal: data.pendingTransversal,
            })}
            to="/validation"
            accent={pendingTotal > 0 ? "#b45309" : undefined}
          />
          <KpiCard
            value={processPct != null ? `${processPct}%` : "—"}
            label={t("dashboard_kpi_process")}
            detail={t("dashboard_kpi_process_detail", {
              inProgress: data.processInProgress,
              validated: data.processValidated,
              total: data.processTotal,
            })}
            to={`/campaigns?tab=suivi&year=${year}`}
          />
          <KpiCard
            value={transversalPct != null ? `${transversalPct}%` : "—"}
            label={t("dashboard_kpi_transversal")}
            detail={t("dashboard_kpi_transversal_detail", {
              inProgress: data.transversalInProgress,
              validated: data.transversalValidated,
              total: data.transversalTotal,
            })}
            to={`/campaigns?tab=suivi&year=${year}`}
          />
          <KpiCard
            value={featuresPct != null ? `${featuresPct}%` : "—"}
            label={t("dashboard_kpi_features")}
            detail={t("dashboard_kpi_features_detail", {
              completed: data.featuresCompleted,
              total: data.featuresTotal,
            })}
            to={`/campaigns?tab=suivi&year=${year}`}
          />
        </div>
      ) : null}

      <p className="sm-muted" style={{ marginTop: 20 }}>
        {t("dashboard_detail_hint")}{" "}
        <Link to={`/campaigns?tab=suivi&year=${year}`}>{t("campaign_tab_suivi")}</Link>
      </p>
    </div>
  );
}
